<script setup lang="ts">
import { NLayoutSider, NMenu } from 'naive-ui'
import { computed, onMounted } from 'vue'
// import logoDark from '@/assets/classroom-v2/nextSchoolDark.png'
// import logoLight from '@/assets/classroom-v2/nextSchool.png'
import logoDark from '@/assets/login/logoLoginDark.png'
import logoLight from '@/assets/login/logoLoginLight.png'
import logoCollapsed from '@/assets/login/logoIcon.png'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { useTheme } from '@/hooks/useTheme'
import { useSiderMenuStudent } from '@/components/layout/sider/composables/useSiderMenu'
import { useClassroomStore, useUserStore } from '@/store'
import Footer from '@/components/layout/sider/FooterClassroom.vue'
import { useNavigation } from '@/components/layout/sider/composables'

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  isDrawer: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:collapsed', 'menu-click'])

const { isMobile } = useBasicLayout()
const { isDark } = useTheme()
const userStore = useUserStore()
const classroomStore = useClassroomStore()
const {
  selectedKeyClassroom,
  handleSelect,
} = useNavigation()

const collapsed = computed(() => props.collapsed)
const { listMenu, expandedKeys } = useSiderMenuStudent(collapsed)

const handleMenuSelect = (key: string) => {
  handleSelect(key)
  if (props.isDrawer)
    emit('menu-click')
}

const workSpaceId = computed(() => userStore.workSpaceId)

const handleUpdateCollapsed = () => {
  emit('update:collapsed', collapsed.value)
}

// Load danh sách classroom khi component mount
onMounted(async () => {
  if (workSpaceId.value && classroomStore.getClassrooms.length === 0) {
    try {
      await classroomStore.fetchClassrooms(workSpaceId.value)
    }
    catch (error) {
      console.error('Error loading classrooms in sidebar:', error)
    }
  }
})
</script>

<template>
  <!-- Drawer Mode (Mobile/Tablet) -->
  <div v-if="isDrawer" class="flex flex-col h-full bg-white dark:bg-[#1e1e2d]">
    <!-- Main Content -->
    <main class="flex-1 min-h-0 flex flex-col">
      <!-- Navigation Menu -->
      <nav class="flex-1 overflow-auto px-2">
        <NMenu
          :options="listMenu"
          :collapsed="false"
          :value="selectedKeyClassroom"
          :indent="16"
          :icon-size="20"
          :default-expanded-keys="expandedKeys"
          @update:value="handleMenuSelect"
        />
      </nav>
    </main>
    <Footer :collapsed="false" :is-drawer="true" />
  </div>

  <!-- Desktop Sider Mode -->
  <NLayoutSider
    v-else
    :collapsed="collapsed"
    :collapsed-width="70"
    :width="260"
    :show-trigger="!isMobile && 'arrow-circle'"
    collapse-mode="width"
    position="absolute"
    bordered
    class="transition-all duration-300 ease-in-out shadow-lg desktop-sider"
    @update-collapsed="handleUpdateCollapsed"
  >
    <div class="flex flex-col h-full bg-white dark:bg-[#1e1e2d]">
      <!-- Header with Logo -->
      <header class="flex items-center" :class="{ 'justify-center px-2': collapsed, 'px-6': !collapsed }">
        <RouterLink to="/agent-management" class="flex items-center py-4">
          <img
            v-if="!collapsed"
            :src="isDark ? logoDark : logoLight"
            alt="Logo"
          >
          <img
            v-else
            :src="logoCollapsed"
            class="w-10"
            alt="Logo Collapsed"
          >
        </RouterLink>
      </header>

      <!-- Main Content -->
      <main class="flex-1 overflow-hidden flex flex-col">
        <!-- Navigation Menu -->
        <nav class="flex-1 overflow-auto px-2">
          <NMenu
            :options="listMenu"
            :collapsed="collapsed"
            :collapsed-width="52"
            :value="selectedKeyClassroom"
            :indent="16"
            :icon-size="20"
            :collapsed-icon-size="20"
            :default-expanded-keys="expandedKeys"
            :class="[selectedKeyClassroom === 'classroom.student.board' ? 'custom-student-board' : '']"
            @update:value="handleSelect"
          />
        </nav>
      </main>
      <Footer :collapsed="collapsed" />
    </div>
  </NLayoutSider>
</template>

<style>
.desktop-sider {
  position: fixed !important;
  height: 100vh;
}
</style>

<style scoped>
:deep(.n-menu-item-content) {
	padding-right: 16px;
	font-weight: 400;
	font-size: 14px;
	opacity: 0.9;
	color: #666666 !important;
}

:deep(.n-submenu-children .n-menu-item-content) {
	padding-left: 44px !important;
}

:deep(.popover-menu-wrapper .n-menu) {
	background: transparent;
}

:deep(.popover-menu-wrapper .n-menu-item) {
	padding: 8px 12px;
	margin: 2px 0;
	border-radius: 6px;
	transition: all 0.2s ease;
}

:deep(.popover-menu-wrapper .n-menu-item:hover) {
	background-color: #f5f5f5;
	transform: translateY(-1px);
}

:deep(.tool-group-title) {
	font-size: 12px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
	padding: 4px 8px;
}

:deep(.custom-student-board.n-menu .n-menu-item-content.n-menu-item-content--selected .n-menu-item-content__icon .enterprise-icon > div) {
	background:linear-gradient(180deg, #F96 0%, #C6F 100%);
	-webkit-text-fill-color: transparent!important;
}
</style>
